/**
 * OptionInstancePerformance — the "Performance" tab for an option-strategy instance.
 *
 * Reorganised into TWO clear hoods (no redundant info scattered around):
 *   A. "Performance over time" — the equity curve (realised P&L stepped at each close +
 *      live open MTM), with a green/red cloud, entry (▲)/close (▼) markers and a range filter.
 *   B. "Returns & open positions" — clubs the day-wise P&L / Investment / ROI grid with the
 *      live payoff & OI of whatever is still open.
 *
 * It is CYCLE-AWARE: multi-leg engines (DeltaMiner / ThetaMiner / Rudra / …) are driven by
 * their cycles (spread economics → real investment, ROI and a proper spread payoff), while
 * the legacy single-leg engines still work off positions + the decision-log snapshots.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Chip, ToggleButton, ToggleButtonGroup, Collapse, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  ComposedChart, Area, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceArea, Cell,
} from 'recharts';
import { useAppTheme } from '../../hooks/useAppTheme';
import api from '../../services/api';
import {
  Cycle, cycleStats, spreadPayoffInr, spreadStrikeSpan, structureName, cycleLots, netEntryInr,
  USD_TO_INR, cvFor,
} from './cycleFormat';

/** Collapsible section with a clearly-coloured, clickable header (default open). */
const Section: React.FC<{ title: string; right?: React.ReactNode; defaultOpen?: boolean; children: React.ReactNode }>
  = ({ title, right, defaultOpen = true, children }) => {
  const { C } = useAppTheme();
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
        bgcolor: C.accentMuted, borderLeft: `3px solid ${C.accent}`, borderRadius: '4px',
        px: 0.8, py: 0.4, mb: 0.8,
      }} onClick={() => setOpen(o => !o)}>
        <IconButton size="small" sx={{ p: 0.2, color: C.accent, transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 150ms' }}><ExpandMoreIcon fontSize="small" /></IconButton>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.4 }}>{title}</Typography>
        {right && <Box sx={{ ml: 'auto' }} onClick={e => e.stopPropagation()}>{right}</Box>}
      </Box>
      <Collapse in={open} unmountOnExit>{children}</Collapse>
    </Box>
  );
};

const SubHead: React.FC<{ title: string; right?: React.ReactNode }> = ({ title, right }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6, mt: 0.4, flexWrap: 'wrap' }}>
    <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>{title}</Typography>
    {right && <Box sx={{ ml: 'auto' }}>{right}</Box>}
  </Box>
);

/** IST calendar day (YYYY-MM-DD) for a timestamp. */
const istDay = (t: number) => new Date(t).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const istDayLabel = (d: string) => {
  const [y, m, dd] = d.split('-');
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m) - 1] || m;
  return `${dd} ${mon} ${y.slice(-2)}`;
};

// USD_TO_INR + cvFor are imported from ./cycleFormat — ONE frontend source (audit B5/B6).
type RangeKey = '1D' | '1W' | '1M' | 'ALL';
const RANGE_MS: Record<RangeKey, number> = { '1D': 864e5, '1W': 7 * 864e5, '1M': 30 * 864e5, ALL: Infinity };
const CLOSE_ACTIONS = new Set(['SQUAREOFF', 'SQUARE_OFF', 'FLIP']);
const OPEN_ACTIONS = new Set(['SELL']);

/** Parse the expiry (YYYY-MM-DD) out of a Delta option symbol like P-BTC-1700-200626. */
function parseDeltaExpiry(sym: string): string {
  const m = /-(\d{2})(\d{2})(\d{2})$/.exec(sym || '');
  if (!m) return '';
  const [, dd, mm, yy] = m;
  return `20${yy}-${mm}-${dd}`;
}

/** Shared stats — also used by the Options-AT accordion header so both agree (single-leg). */
export function computeInstanceStats(positions: any[], events: any[]) {
  const trades = (positions || []).reduce((s, p) => s + (Number(p.tradeCount) || 0), 0);
  const realized = (positions || []).reduce((s, p) => s + (Number(p.realizedPnl) || 0), 0);
  const asc = [...(events || [])].filter(e => e.pnl != null).sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  const last: Record<string, number> = {};
  for (const e of asc) last[e.symbol] = Number(e.pnl);
  const total = Object.keys(last).length ? Object.values(last).reduce((s, v) => s + v, 0) : realized;
  const open = total - realized;
  const closes = asc.filter(e => CLOSE_ACTIONS.has(e.action));
  let lastTradePnl: number | null = null; let lastTradeAt: string | null = null;
  if (closes.length) {
    const lastC = closes[closes.length - 1];
    const sameSym = asc.filter(e => e.symbol === lastC.symbol);
    const idx = sameSym.findIndex(e => e.id === lastC.id);
    const prevC = [...sameSym.slice(0, idx)].reverse().find(e => CLOSE_ACTIONS.has(e.action));
    lastTradePnl = prevC ? Number(lastC.pnl) - Number(prevC.pnl) : Number(lastC.pnl);
    lastTradeAt = lastC.at;
  }
  const openCount = (positions || []).filter(p => p.status && p.status !== 'FLAT').length;
  return { trades, realized, open, total, lastTradePnl, lastTradeAt, openCount };
}

const inr = (v: number) => `${v >= 0 ? '+' : '−'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;

interface Props { positions: any[]; events: any[]; cycles?: Cycle[]; multiLeg?: boolean }

const OptionInstancePerformance: React.FC<Props> = ({ positions, events, cycles = [], multiLeg = false }) => {
  const { C } = useAppTheme();
  const theme = useTheme();
  const [range, setRange] = useState<RangeKey>('1W');
  const [showUnd, setShowUnd] = useState(false);
  const [ov, setOv] = useState({ payoff: true, oiBars: true, esl: true, maxPain: true, oiSR: true });
  const [oiByUnd, setOiByUnd] = useState<Record<string, any>>({});

  // Shared theme-aware tooltip styling (readable text in light + dark mode).
  const tip = {
    contentStyle: { backgroundColor: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}`, borderRadius: 6, fontSize: 11 },
    labelStyle: { color: theme.palette.text.primary, fontSize: 11, fontWeight: 700 },
    itemStyle: { color: theme.palette.text.primary, fontSize: 11 },
  };

  const openCycles = useMemo(() => (multiLeg ? (cycles || []).filter(c => c.status === 'OPEN') : []), [cycles, multiLeg]);
  const held = useMemo(() => positions.filter(p => p.status !== 'FLAT' && p.heldSymbol), [positions]);

  // Fetch the live OI map for each open underlying+expiry (once per key) — covers both
  // multi-leg open cycles and legacy single-leg held positions.
  const oiKeys = useMemo(() => {
    const keys = new Set<string>();
    if (multiLeg) openCycles.forEach(c => keys.add(`${c.symbol}|${c.meta?.expiry || parseDeltaExpiry(c.legs?.[0]?.legSymbol || '')}`));
    else held.forEach(p => keys.add(`${p.symbol}|${parseDeltaExpiry(p.heldSymbol)}`));
    return Array.from(keys);
  }, [multiLeg, openCycles, held]);

  useEffect(() => {
    oiKeys.forEach(key => {
      if (oiByUnd[key] !== undefined) return;
      const [symbol, exp] = key.split('|');
      setOiByUnd(prev => ({ ...prev, [key]: null })); // in-flight
      api.get<any>('/charts/option-oi-map', { params: { symbol, ...(exp ? { expiry: exp } : {}) } })
        .then(r => setOiByUnd(prev => ({ ...prev, [key]: r.data?.data || {} })))
        .catch(() => setOiByUnd(prev => ({ ...prev, [key]: {} })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oiKeys.join(',')]);

  const stats = useMemo(() => {
    if (multiLeg) { const s = cycleStats(cycles || []); return { trades: s.trades, realized: s.realised, open: s.open, total: s.total, lastTradePnl: s.lastTradePnl }; }
    const s = computeInstanceStats(positions, events); return { trades: s.trades, realized: s.realized, open: s.open, total: s.total, lastTradePnl: s.lastTradePnl };
  }, [multiLeg, cycles, positions, events]);

  // Primary symbol for the underlying overlay (single-leg only) = the one with the most priced events.
  const primarySym = useMemo(() => {
    if (multiLeg) return '';
    const counts: Record<string, number> = {};
    for (const e of events || []) if (e.underlyingLtp != null) counts[e.symbol] = (counts[e.symbol] || 0) + 1;
    return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
  }, [events, multiLeg]);

  // Equity curve. Multi-leg: cumulative realised P&L stepped at each cycle close + a live
  // "now" point that folds in open MTM. Single-leg: the decision-log MTM snapshots.
  const curve = useMemo(() => {
    const cutoff = range === 'ALL' ? -Infinity : Date.now() - RANGE_MS[range];
    const mk = (t: number) => new Date(t).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    if (multiLeg) {
      const marks: Array<{ t: number; delta: number; kind: 'open' | 'close' | null }> = [];
      for (const c of cycles || []) {
        if (c.entryAt) marks.push({ t: new Date(c.entryAt).getTime(), delta: 0, kind: 'open' });
        if (c.status !== 'OPEN' && c.closedAt) marks.push({ t: new Date(c.closedAt).getTime(), delta: Number(c.realizedPnl) || 0, kind: 'close' });
      }
      marks.sort((a, b) => a.t - b.t);
      let cum = 0;
      const out: Array<{ t: number; total: number; label: string; kind: 'open' | 'close' | null; und: number | null }> = [];
      for (const m of marks) { cum += m.delta; if (m.t < cutoff) continue; out.push({ t: m.t, total: Math.round(cum), label: mk(m.t), kind: m.kind, und: null }); }
      const openMtm = (cycles || []).filter(c => c.status === 'OPEN').reduce((s, c) => s + (Number(c.openPnlInr) || 0), 0);
      if ((cycles || []).some(c => c.status === 'OPEN')) out.push({ t: Date.now(), total: Math.round(cum + openMtm), label: 'now', kind: null, und: null });
      return out;
    }
    const asc = [...(events || [])].filter(e => e.pnl != null).sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
    const lastBySym: Record<string, number> = {};
    let lastUnd: number | null = null;
    const out: Array<{ t: number; total: number; label: string; kind: 'open' | 'close' | null; und: number | null }> = [];
    for (const e of asc) {
      lastBySym[e.symbol] = Number(e.pnl);
      if (e.symbol === primarySym && e.underlyingLtp != null) lastUnd = Number(e.underlyingLtp);
      const t = new Date(e.at).getTime();
      if (t < cutoff) continue;
      const total = Object.values(lastBySym).reduce((s, v) => s + v, 0);
      const kind = CLOSE_ACTIONS.has(e.action) ? 'close' : OPEN_ACTIONS.has(e.action) ? 'open' : null;
      out.push({ t, total: Math.round(total), label: mk(t), kind, und: lastUnd });
    }
    return out;
  }, [events, range, primarySym, multiLeg, cycles]);

  const splitOffset = useMemo(() => {
    if (!curve.length) return 0.5;
    const max = Math.max(...curve.map(d => d.total));
    const min = Math.min(...curve.map(d => d.total));
    if (max <= 0) return 0;
    if (min >= 0) return 1;
    return max / (max - min);
  }, [curve]);

  const flatSpans = useMemo(() => {
    const spans: Array<{ x1: string; x2: string }> = [];
    let openClose: string | null = null;
    for (const pt of curve) {
      if (pt.kind === 'close') openClose = pt.label;
      else if (pt.kind === 'open' && openClose) { spans.push({ x1: openClose, x2: pt.label }); openClose = null; }
    }
    return spans;
  }, [curve]);

  const dayRows = useMemo(() => {
    if (multiLeg) {
      const byDay = new Map<string, { pnl: number; invested: number }>();
      for (const c of (cycles || []).filter(c => c.status !== 'OPEN' && c.closedAt)) {
        const day = istDay(new Date(c.closedAt as string).getTime());
        const cur = byDay.get(day) || { pnl: 0, invested: 0 };
        cur.pnl += Number(c.realizedPnl) || 0;
        cur.invested += Number(c.investedInr) || 0;
        byDay.set(day, cur);
      }
      const today = istDay(Date.now());
      const opens = (cycles || []).filter(c => c.status === 'OPEN');
      if (opens.length) {
        const cur = byDay.get(today) || { pnl: 0, invested: 0 };
        cur.pnl += opens.reduce((s, c) => s + (Number(c.openPnlInr) || 0), 0);
        cur.invested += opens.reduce((s, c) => s + (Number(c.investedInr) || 0), 0);
        byDay.set(today, cur);
      }
      return Array.from(byDay.entries())
        .map(([day, v]) => ({ day, pnl: v.pnl, invested: v.invested, roi: v.invested > 0 ? (v.pnl / v.invested) * 100 : null }))
        .sort((a, b) => b.day.localeCompare(a.day));
    }
    const asc = [...(events || [])].filter(e => e.pnl != null).sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
    const lastPnl: Record<string, number> = {};
    const lastInv: Record<string, number> = {};
    const byDay = new Map<string, { endTotal: number; peakInv: number }>();
    for (const e of asc) {
      lastPnl[e.symbol] = Number(e.pnl);
      if (e.invested != null) lastInv[e.symbol] = Number(e.invested);
      const day = istDay(new Date(e.at).getTime());
      const total = Object.values(lastPnl).reduce((s, v) => s + v, 0);
      const invAll = Object.values(lastInv).reduce((s, v) => s + v, 0);
      const cur = byDay.get(day) || { endTotal: 0, peakInv: 0 };
      cur.endTotal = total;
      cur.peakInv = Math.max(cur.peakInv, invAll);
      byDay.set(day, cur);
    }
    const days = Array.from(byDay.keys()).sort();
    const rows: Array<{ day: string; pnl: number; invested: number; roi: number | null }> = [];
    let prevEnd = 0;
    for (const d of days) {
      const { endTotal, peakInv } = byDay.get(d)!;
      const pnl = endTotal - prevEnd;
      prevEnd = endTotal;
      rows.push({ day: d, pnl, invested: peakInv, roi: peakInv > 0 ? (pnl / peakInv) * 100 : null });
    }
    return rows.reverse();
  }, [events, cycles, multiLeg]);

  /** Position cost basis for the legacy single-leg payoff/OI chart. */
  const payInfo = (pos: any) => {
    const strike = Number(pos.heldStrike);
    const premium = Number(pos.entryPremium);
    const qty = Number(pos.qty) || 1;
    const isCE = pos.heldOptType === 'CE';
    const cv = cvFor(pos.symbol);
    const spot = Number(pos.lastUnderlyingLtp) || strike;
    if (!(strike > 0) || !(premium > 0)) return null;
    const mult = qty * cv * USD_TO_INR;
    const pnlAt = (s: number) => Math.round((premium - (isCE ? Math.max(0, s - strike) : Math.max(0, strike - s))) * mult);
    const breakEven = isCE ? strike + premium : strike - premium;
    return { strike, premium, qty, isCE, cv, spot, mult, pnlAt, breakEven, esl: pos.eslLevel != null ? Number(pos.eslLevel) : null };
  };

  const Stat: React.FC<{ label: string; value: React.ReactNode; color?: string }> = ({ label, value, color }) => (
    <Box sx={{ minWidth: 96 }}>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</Typography>
      <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: color || 'text.primary' }}>{value}</Typography>
    </Box>
  );

  const hasOpen = multiLeg ? openCycles.length > 0 : held.length > 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1.5 }}>
        <Stat label="Trades" value={stats.trades} />
        <Stat label="Realised P&L" value={inr(stats.realized)} color={stats.realized >= 0 ? C.bull : C.bear} />
        <Stat label="Open P&L" value={inr(stats.open)} color={stats.open >= 0 ? C.bull : C.bear} />
        <Stat label="Total P&L" value={inr(stats.total)} color={stats.total >= 0 ? C.bull : C.bear} />
        {stats.lastTradePnl != null && <Stat label="Last trade" value={inr(stats.lastTradePnl)} color={stats.lastTradePnl >= 0 ? C.bull : C.bear} />}
      </Box>

      {/* ── HOOD A — Performance over time ── */}
      <Section title="Performance over time" right={
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {primarySym && (
            <Chip size="small" label={`${primarySym} price`} variant={showUnd ? 'filled' : 'outlined'} color={showUnd ? 'primary' : 'default'}
              onClick={() => setShowUnd(s => !s)} sx={{ height: 22, fontSize: '0.62rem', cursor: 'pointer' }} />
          )}
          <ToggleButtonGroup size="small" exclusive value={range} onChange={(_e, v) => v && setRange(v)} sx={{ '& .MuiToggleButton-root': { py: 0, px: 0.8, fontSize: '0.62rem' } }}>
            {(['1D', '1W', '1M', 'ALL'] as RangeKey[]).map(k => <ToggleButton key={k} value={k}>{k}</ToggleButton>)}
          </ToggleButtonGroup>
        </Box>
      }>
      {curve.length < 2 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Not enough data in this range — the curve builds up as trades close.</Typography>
      ) : (
        <Box sx={{ height: 230, mb: 1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={curve} margin={{ top: 6, right: showUnd ? 8 : 12, bottom: 4, left: 4 }}>
              <defs>
                <linearGradient id="pnlSplit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset={0} stopColor={C.bull} stopOpacity={0.5} />
                  <stop offset={splitOffset} stopColor={C.bull} stopOpacity={0.06} />
                  <stop offset={splitOffset} stopColor={C.bear} stopOpacity={0.06} />
                  <stop offset={1} stopColor={C.bear} stopOpacity={0.5} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} minTickGap={40} />
              <YAxis yAxisId="pnl" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} width={48} tickFormatter={(v: number) => `₹${Math.round(v)}`} />
              {showUnd && <YAxis yAxisId="und" orientation="right" tick={{ fontSize: 10, fill: C.spot }} width={52} domain={['auto', 'auto']} tickFormatter={(v: number) => Math.round(v).toLocaleString()} />}
              <Tooltip {...tip} formatter={(v: any, name: any) => name === 'und' ? [Number(v).toLocaleString('en-IN'), `${primarySym}`] : [`₹${Math.round(Number(v)).toLocaleString('en-IN')}`, 'Total P&L']} />
              {flatSpans.map((s, i) => <ReferenceArea key={i} yAxisId="pnl" x1={s.x1} x2={s.x2} fill={C.dim} fillOpacity={0.12} stroke="none" ifOverflow="extendDomain" />)}
              <ReferenceLine yAxisId="pnl" y={0} stroke={C.chartGrid} />
              <Area yAxisId="pnl" type="monotone" dataKey="total" stroke="none" fill="url(#pnlSplit)" isAnimationActive={false} />
              <Line yAxisId="pnl" type="monotone" dataKey="total" stroke={stats.total >= 0 ? C.bull : C.bear} strokeWidth={2} isAnimationActive={false}
                dot={(props: any) => {
                  const k = props?.payload?.kind;
                  if (k === 'open') return <path key={props.index} d={`M${props.cx},${props.cy - 5} L${props.cx - 5},${props.cy + 4} L${props.cx + 5},${props.cy + 4} Z`} fill={C.neutral} stroke={C.chartTooltipBg} strokeWidth={1} />;
                  if (k === 'close') return <path key={props.index} d={`M${props.cx},${props.cy + 5} L${props.cx - 5},${props.cy - 4} L${props.cx + 5},${props.cy - 4} Z`} fill={C.warn} stroke={C.chartTooltipBg} strokeWidth={1} />;
                  return <circle key={props.index} cx={props.cx} cy={props.cy} r={0} fill="none" />;
                }} />
              {showUnd && <Line yAxisId="und" type="monotone" dataKey="und" stroke={C.spot} strokeWidth={1.4} dot={false} strokeDasharray="4 2" isAnimationActive={false} connectNulls />}
            </ComposedChart>
          </ResponsiveContainer>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.3 }}>
            <LegendDot color={C.neutral} shape="up" label="Position opened" />
            <LegendDot color={C.warn} shape="down" label="Squared off" />
            <LegendDot color={C.dim} shape="box" label="No position running" />
            {showUnd && <LegendDot color={C.spot} shape="line" label={`${primarySym} price`} />}
          </Box>
        </Box>
      )}
      </Section>

      {/* ── HOOD B — Returns & open positions (day-wise + payoff/OI clubbed) ── */}
      <Section title="Returns & open positions">
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Day-by-day realised / live P&L on the capital at work, and the live payoff &amp; OI of anything still open.
        </Typography>

        <SubHead title="Day-wise P&L / ROI" />
        {dayRows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>No day-wise data yet.</Typography>
        ) : (
          <Box sx={{ maxHeight: 260, overflow: 'auto', border: `1px solid ${C.chartGrid}`, borderRadius: 1, mb: 1.5 }}>
            <Table size="small" stickyHeader>
              <TableHead><TableRow>{['Date', 'Investment', 'Day P&L', 'ROI'].map((h, i) => <TableCell key={h} align={i === 0 ? 'left' : 'right'} sx={{ fontWeight: 700, fontSize: '0.66rem', px: 1 }}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {dayRows.map(r => (
                  <TableRow key={r.day}>
                    <TableCell sx={{ fontSize: '0.7rem', px: 1 }}>{istDayLabel(r.day)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', px: 1 }}>{r.invested > 0 ? `₹${Math.round(r.invested).toLocaleString('en-IN')}` : '—'}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.72rem', px: 1, fontWeight: 700, color: r.pnl >= 0 ? 'success.main' : 'error.main' }}>{inr(r.pnl)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', px: 1, fontWeight: 700, color: r.roi == null ? 'text.disabled' : r.roi >= 0 ? 'success.main' : 'error.main' }}>{r.roi == null ? '—' : `${r.roi >= 0 ? '+' : ''}${r.roi.toFixed(2)}%`}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <SubHead title="Open positions — payoff & OI" right={
          hasOpen ? (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {([['payoff', 'Payoff'], ['oiBars', 'OI walls'], ['esl', 'Emergency SL'], ['maxPain', 'Max pain'], ['oiSR', 'OI S/R']] as [keyof typeof ov, string][]).map(([k, lbl]) => (
                <Chip key={k} size="small" label={lbl} variant={ov[k] ? 'filled' : 'outlined'} color={ov[k] ? 'primary' : 'default'}
                  onClick={() => setOv(prev => ({ ...prev, [k]: !prev[k] }))} sx={{ height: 22, fontSize: '0.62rem', cursor: 'pointer' }} />
              ))}
            </Box>
          ) : undefined
        } />
        {!hasOpen ? (
          <Typography variant="body2" color="text.secondary">No open positions. The payoff &amp; OI walls appear here once a trade is running.</Typography>
        ) : multiLeg ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {openCycles.map(c => {
              const exp = c.meta?.expiry || parseDeltaExpiry(c.legs?.[0]?.legSymbol || '');
              const oi = oiByUnd[`${c.symbol}|${exp}`];
              const span = spreadStrikeSpan(c);
              if (!span) return <Typography key={c.id} variant="caption" color="text.secondary">{c.symbol}: payoff unavailable (missing strikes).</Typography>;
              const { lo, hi } = span;
              const maxPain = oi?.maxPain ?? null;
              const supports: number[] = oi?.supports || [];
              const resistances: number[] = oi?.resistances || [];
              const oiRows: any[] = (oi?.rows || []).filter((r: any) => r.strike >= lo && r.strike <= hi);
              const hasOi = oiRows.length > 0;
              const maxOi = hasOi ? Math.max(...oiRows.flatMap((r: any) => [Number(r.ceOi) || 0, Number(r.peOi) || 0])) : 0;
              let data: any[];
              if (hasOi) {
                data = oiRows.sort((a: any, b: any) => a.strike - b.strike).map((r: any) => ({
                  strike: r.strike, pnl: Math.round(spreadPayoffInr(c, r.strike) || 0),
                  ceK: (Number(r.ceOi) || 0) / 1000, peK: -(Number(r.peOi) || 0) / 1000,
                }));
              } else {
                data = []; const N = 61;
                for (let i = 0; i < N; i++) { const s = Math.round(lo + (hi - lo) * (i / (N - 1))); data.push({ strike: s, pnl: Math.round(spreadPayoffInr(c, s) || 0), ceK: 0, peK: 0 }); }
              }
              const optType = c.legs?.[0]?.optType;
              const name = structureName(c.meta?.kind, c.meta?.avSide, optType);
              const entryInr = netEntryInr(c);
              const spot = Number(c.entrySpot) || Math.round((lo + hi) / 2);
              return (
                <Box key={c.id}>
                  <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, mb: 0.3 }}>
                    {c.symbol} · {name} {c.meta?.shortStrike}/{c.meta?.longStrike} ×{cycleLots(c)} {exp ? `· ${exp}` : ''}
                    {entryInr != null && <Typography component="span" sx={{ fontSize: '0.66rem', color: 'text.secondary', ml: 1 }}>{entryInr >= 0 ? 'credit' : 'debit'} {`${entryInr >= 0 ? '+' : '−'}₹${Math.abs(entryInr).toLocaleString('en-IN')}`}</Typography>}
                    {maxPain != null && <Typography component="span" sx={{ fontSize: '0.66rem', color: 'text.secondary', ml: 1 }}>Max pain {Number(maxPain).toLocaleString()}</Typography>}
                    {oi === null && <Typography component="span" sx={{ fontSize: '0.62rem', color: 'text.disabled', ml: 1 }}>loading OI…</Typography>}
                  </Typography>
                  <Box sx={{ bgcolor: C.accentMuted, borderRadius: 1.5, p: 1 }}>
                    <Box sx={{ height: 280 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={data} margin={{ top: 6, right: 12, bottom: 4, left: 4 }}>
                          <defs>
                            <linearGradient id={`payc${c.id}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={C.bull} stopOpacity={0.4} />
                              <stop offset="100%" stopColor={C.bull} stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
                          <XAxis dataKey="strike" type="number" domain={['dataMin', 'dataMax']} tick={{ fontSize: 9, fill: theme.palette.text.secondary }} tickFormatter={(v: number) => v.toLocaleString()} minTickGap={28} />
                          <YAxis yAxisId="pnl" tick={{ fontSize: 9, fill: theme.palette.text.secondary }} width={48} tickFormatter={(v: number) => `₹${v}`} />
                          <YAxis yAxisId="oi" orientation="right" tick={{ fontSize: 9, fill: theme.palette.text.secondary }} width={40} tickFormatter={(v: number) => `${Math.abs(v).toFixed(0)}K`} domain={maxOi > 0 ? [-(maxOi / 1000) * 1.1, (maxOi / 1000) * 1.1] : [-1, 1]} />
                          <Tooltip {...tip} labelFormatter={(l) => `Spot ${Number(l).toLocaleString()}`}
                            formatter={(v: any, name2: any) => {
                              if (name2 === 'pnl') return [`₹${Math.round(Number(v)).toLocaleString('en-IN')}`, 'P&L at expiry'];
                              if (name2 === 'ceK') return [`${Math.abs(Number(v)).toFixed(1)}K`, 'CE OI (resistance)'];
                              if (name2 === 'peK') return [`${Math.abs(Number(v)).toFixed(1)}K`, 'PE OI (support)'];
                              return [v, name2];
                            }} />
                          <ReferenceLine yAxisId="oi" y={0} stroke={C.chartGrid} />
                          {ov.oiBars && hasOi && <Bar yAxisId="oi" dataKey="ceK" barSize={6} radius={[2, 2, 0, 0]} isAnimationActive={false}>{data.map((_, i) => <Cell key={i} fill={C.bear} fillOpacity={0.55} />)}</Bar>}
                          {ov.oiBars && hasOi && <Bar yAxisId="oi" dataKey="peK" barSize={6} radius={[0, 0, 2, 2]} isAnimationActive={false}>{data.map((_, i) => <Cell key={i} fill={C.bull} fillOpacity={0.55} />)}</Bar>}
                          {ov.payoff && <Area yAxisId="pnl" type="monotone" dataKey="pnl" stroke={C.bull} strokeWidth={1.8} fill={`url(#payc${c.id})`} isAnimationActive={false} />}
                          <ReferenceLine yAxisId="pnl" y={0} stroke={C.chartGrid} />
                          {c.meta?.shortStrike != null && <ReferenceLine yAxisId="pnl" x={Number(c.meta.shortStrike)} stroke={C.bear} strokeDasharray="4 3" label={{ value: 'Short', fontSize: 9, fill: C.bear }} />}
                          {c.meta?.longStrike != null && <ReferenceLine yAxisId="pnl" x={Number(c.meta.longStrike)} stroke={C.bull} strokeDasharray="4 3" label={{ value: 'Long', fontSize: 9, fill: C.bull }} />}
                          <ReferenceLine yAxisId="pnl" x={Math.round(spot)} stroke={C.spot} strokeWidth={1.5} label={{ value: 'Entry spot', fontSize: 9, fill: C.spot }} />
                          {ov.maxPain && maxPain != null && <ReferenceLine yAxisId="pnl" x={Math.round(Number(maxPain))} stroke={C.maxPain} strokeWidth={1.6} strokeDasharray="5 3" label={{ value: 'MaxPain', fontSize: 10, fill: C.maxPain }} />}
                          {ov.oiSR && supports.filter(s => s >= lo && s <= hi).map(s => <ReferenceLine key={`sup${s}`} yAxisId="pnl" x={s} stroke={C.bull} strokeDasharray="1 3" label={{ value: 'OI-S', fontSize: 9, fill: C.bull }} />)}
                          {ov.oiSR && resistances.filter(s => s >= lo && s <= hi).map(s => <ReferenceLine key={`res${s}`} yAxisId="pnl" x={s} stroke={C.bear} strokeDasharray="1 3" label={{ value: 'OI-R', fontSize: 9, fill: C.bear }} />)}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.3, px: 1 }}>
                      <LegendDot color={C.bull} shape="box" label="Payoff / PE OI (support)" />
                      <LegendDot color={C.bear} shape="box" label="CE OI (resistance)" />
                      {!hasOi && ov.oiBars && <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>OI walls unavailable (no live chain OI)</Typography>}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {held.map(p => {
              const pi = payInfo(p);
              const oi = oiByUnd[`${p.symbol}|${parseDeltaExpiry(p.heldSymbol)}`];
              const maxPain = oi?.maxPain ?? null;
              const supports: number[] = oi?.supports || [];
              const resistances: number[] = oi?.resistances || [];
              if (!pi) return <Typography key={p.id} variant="caption" color="text.secondary">{p.symbol}: payoff unavailable (missing strike/premium).</Typography>;
              const lo = pi.strike * 0.85, hi = pi.strike * 1.15;
              const oiRows: any[] = (oi?.rows || []).filter((r: any) => r.strike >= lo && r.strike <= hi);
              const hasOi = oiRows.length > 0;
              const maxOi = hasOi ? Math.max(...oiRows.flatMap((r: any) => [Number(r.ceOi) || 0, Number(r.peOi) || 0])) : 0;
              let data: any[];
              if (hasOi) {
                data = oiRows.sort((a: any, b: any) => a.strike - b.strike).map((r: any) => ({
                  strike: r.strike, pnl: pi.pnlAt(r.strike),
                  ceK: (Number(r.ceOi) || 0) / 1000, peK: -(Number(r.peOi) || 0) / 1000,
                }));
              } else {
                data = []; const N = 61;
                for (let i = 0; i < N; i++) { const s = Math.round(lo + (hi - lo) * (i / (N - 1))); data.push({ strike: s, pnl: pi.pnlAt(s), ceK: 0, peK: 0 }); }
              }
              return (
                <Box key={p.id}>
                  <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, mb: 0.3 }}>
                    {p.symbol} · SHORT {p.heldSymbol} ×{p.qty} @ {p.entryPremium}
                    {maxPain != null && <Typography component="span" sx={{ fontSize: '0.66rem', color: 'text.secondary', ml: 1 }}>Max pain {Number(maxPain).toLocaleString()}</Typography>}
                    {oi === null && <Typography component="span" sx={{ fontSize: '0.62rem', color: 'text.disabled', ml: 1 }}>loading OI…</Typography>}
                  </Typography>
                  <Box sx={{ bgcolor: C.accentMuted, borderRadius: 1.5, p: 1 }}>
                    <Box sx={{ height: 280 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={data} margin={{ top: 6, right: 12, bottom: 4, left: 4 }}>
                          <defs>
                            <linearGradient id={`pay${p.id}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={C.bull} stopOpacity={0.4} />
                              <stop offset="100%" stopColor={C.bull} stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
                          <XAxis dataKey="strike" type="number" domain={['dataMin', 'dataMax']} tick={{ fontSize: 9, fill: theme.palette.text.secondary }} tickFormatter={(v: number) => v.toLocaleString()} minTickGap={28} />
                          <YAxis yAxisId="pnl" tick={{ fontSize: 9, fill: theme.palette.text.secondary }} width={48} tickFormatter={(v: number) => `₹${v}`} />
                          <YAxis yAxisId="oi" orientation="right" tick={{ fontSize: 9, fill: theme.palette.text.secondary }} width={40} tickFormatter={(v: number) => `${Math.abs(v).toFixed(0)}K`} domain={maxOi > 0 ? [-(maxOi / 1000) * 1.1, (maxOi / 1000) * 1.1] : [-1, 1]} />
                          <Tooltip {...tip} labelFormatter={(l) => `Spot ${Number(l).toLocaleString()}`}
                            formatter={(v: any, name2: any) => {
                              if (name2 === 'pnl') return [`₹${Math.round(Number(v)).toLocaleString('en-IN')}`, 'P&L at expiry'];
                              if (name2 === 'ceK') return [`${Math.abs(Number(v)).toFixed(1)}K`, 'CE OI (resistance)'];
                              if (name2 === 'peK') return [`${Math.abs(Number(v)).toFixed(1)}K`, 'PE OI (support)'];
                              return [v, name2];
                            }} />
                          <ReferenceLine yAxisId="oi" y={0} stroke={C.chartGrid} />
                          {ov.oiBars && hasOi && <Bar yAxisId="oi" dataKey="ceK" barSize={6} radius={[2, 2, 0, 0]} isAnimationActive={false}>{data.map((_, i) => <Cell key={i} fill={C.bear} fillOpacity={0.55} />)}</Bar>}
                          {ov.oiBars && hasOi && <Bar yAxisId="oi" dataKey="peK" barSize={6} radius={[0, 0, 2, 2]} isAnimationActive={false}>{data.map((_, i) => <Cell key={i} fill={C.bull} fillOpacity={0.55} />)}</Bar>}
                          {ov.payoff && <Area yAxisId="pnl" type="monotone" dataKey="pnl" stroke={C.bull} strokeWidth={1.8} fill={`url(#pay${p.id})`} isAnimationActive={false} />}
                          <ReferenceLine yAxisId="pnl" y={0} stroke={C.chartGrid} />
                          <ReferenceLine yAxisId="pnl" x={pi.strike} stroke={C.dim} strokeDasharray="4 3" label={{ value: 'K', fontSize: 10, fill: C.dim }} />
                          <ReferenceLine yAxisId="pnl" x={Math.round(pi.spot)} stroke={C.spot} strokeWidth={1.5} label={{ value: 'Spot', fontSize: 10, fill: C.spot }} />
                          <ReferenceLine yAxisId="pnl" x={Math.round(pi.breakEven)} stroke={C.accent} strokeDasharray="2 2" label={{ value: 'BE', fontSize: 10, fill: C.accent }} />
                          {ov.esl && pi.esl != null && <ReferenceLine yAxisId="pnl" x={Math.round(pi.esl)} stroke={C.bear} strokeWidth={1.6} label={{ value: 'ESL', fontSize: 10, fill: C.bear }} />}
                          {ov.maxPain && maxPain != null && <ReferenceLine yAxisId="pnl" x={Math.round(Number(maxPain))} stroke={C.maxPain} strokeWidth={1.6} strokeDasharray="5 3" label={{ value: 'MaxPain', fontSize: 10, fill: C.maxPain }} />}
                          {ov.oiSR && supports.filter(s => s >= lo && s <= hi).map(s => <ReferenceLine key={`sup${s}`} yAxisId="pnl" x={s} stroke={C.bull} strokeDasharray="1 3" label={{ value: 'OI-S', fontSize: 9, fill: C.bull }} />)}
                          {ov.oiSR && resistances.filter(s => s >= lo && s <= hi).map(s => <ReferenceLine key={`res${s}`} yAxisId="pnl" x={s} stroke={C.bear} strokeDasharray="1 3" label={{ value: 'OI-R', fontSize: 9, fill: C.bear }} />)}
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 0.3, px: 1 }}>
                      <LegendDot color={C.bull} shape="box" label="Payoff / PE OI (support)" />
                      <LegendDot color={C.bear} shape="box" label="CE OI (resistance)" />
                      {!hasOi && ov.oiBars && <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>OI walls unavailable (no live chain OI)</Typography>}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Section>
    </Box>
  );
};

const LegendDot: React.FC<{ color: string; shape: 'up' | 'down' | 'box' | 'line'; label: string }> = ({ color, shape, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
    <Box sx={{
      width: shape === 'line' ? 14 : 0, height: 0,
      ...(shape === 'up' ? { borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderBottom: `8px solid ${color}` }
        : shape === 'down' ? { borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `8px solid ${color}` }
        : shape === 'line' ? { borderTop: `2px dashed ${color}`, height: 0 }
        : { width: 10, height: 10, bgcolor: color, borderRadius: 0.3 }),
    }} />
    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>{label}</Typography>
  </Box>
);

export default OptionInstancePerformance;
